/**
 * Analytics utility for tracking user events via Google Tag Manager dataLayer
 * Provides safe event tracking with error handling to prevent application crashes
 */

/**
 * Interface for dataLayer events
 */
interface DataLayerEvent {
  event: string
  [key: string]: string | number | boolean | undefined
}

/**
 * Params accepted by GA4. Values must be flat scalars — GA4 cannot index
 * nested objects, so a nested value is unusable as a custom dimension.
 */
type GtagEventParams = Record<
  string,
  string | number | boolean | undefined | null
>

/**
 * The event payloads trackEvent accepts. The typed interfaces below carry no
 * index signature, so they are named explicitly rather than being assignable
 * to GtagEventParams.
 */
type TrackableEventData = SignupEventData | WizardEventData | GtagEventParams

/**
 * Interface for signup-related event data
 */
interface SignupEventData {
  step?: string
  account_type?: 'personal' | 'business' | 'league'
  user_type?: string
  email_hash?: string
  company_name?: string
  context?: string
  context_id?: string
  from_step?: number
  to_step?: number
}

/**
 * Wizard tracking types
 */
type WizardName = 'onboarding' | 'competition'
type WizardAction =
  | 'start'
  | 'skip'
  | 'close'
  | 'continue'
  | 'finish'
  // step-specific "verbs"
  | 'league_layout'
  | 'sports_setup'
  | 'first_competition'
  | 'structure'
  | 'division'
  | 'conference'

/**
 * Interface for wizard-related event data
 */
interface WizardEventData {
  step?: string // e.g., 'get_started', 'league_layout', 'structure'
  choice?: string // e.g., 'simple_league' | 'specialist' | 'single_division' | ...
  sport_count?: number
  competition_name?: string
  sport?: string
  subleague?: string
  division_name?: string
  conference_count?: number
  from_step?: number
  to_step?: number
  context?: string
  context_id?: string
  user_type?: string
  account_type?: string
  email_hash?: string
}

/**
 * Creates a simple hash from a string (for privacy-safe email tracking)
 * @param str - String to hash
 * @returns Simple hash of the input string
 */
const createSimpleHash = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Events the SportNinja GTM container (GTM-KHQWLHD) forwards to GA4 itself,
 * via a configured trigger + tag.
 *
 * A `dataLayer` push only reaches GA4 if something reads it. SportNinja loads
 * GTM, which has triggers for the events below, so pushing is enough — calling
 * gtag() for them too would double-count. Canlan has no container
 * (public_assets/canlan/index.html loads gtag.js only), so nothing reads its
 * pushes and every event needs the direct call.
 *
 * The list is per-event but not per-tenant, so it is only half the decision:
 * see hasTagManagerContainer for the other half.
 *
 * ⚠️ When a trigger is added or removed in the GTM UI, update this list in the
 * same change: a missing entry double-counts, a stale one drops the event.
 */
export const GTM_FORWARDED_EVENTS: ReadonlySet<string> = new Set<string>([
  'registration_step',
])

/**
 * Whether this tenant's build ships a GTM container.
 *
 * Read from build config rather than detected at runtime. `env-setup.sh` copies
 * `public_assets/$CUSTOMER_NAME/index.html` into place, so whether GTM loads is
 * already decided per tenant at build time; `REACT_APP_GTM_CONTAINER_ID`
 * records that same fact for the app.
 *
 * Probing the page instead is unreliable: gtag.js populates
 * `window.google_tag_manager` for its own bookkeeping (measurement id,
 * `dataLayer`, `sequence`, ...) even where no container is installed, so a
 * truthiness check on it reports "GTM present" on Canlan and silently
 * suppresses every event there.
 */
const hasTagManagerContainer = (): boolean =>
  Boolean(process.env.REACT_APP_GTM_CONTAINER_ID)

/**
 * Sends an event straight to GA4 via gtag(), bypassing GTM.
 *
 * GA4 rejects nested objects, so callers must pass flat scalar params (the
 * GtagEventParams type enforces this).
 *
 * @param eventName - Name of the event to track
 * @param eventData - Flat scalar params to attach to the event
 */
const sendDirectToGa4 = (
  eventName: string,
  eventData: GtagEventParams
): void => {
  const gtag = (window as GtagWindow).gtag
  if (typeof gtag !== 'function') {
    return
  }
  gtag('event', eventName, eventData)
}

/**
 * Safely tracks an event.
 *
 * Always pushes to `dataLayer` (consumed by GTM where a container exists), and
 * additionally calls gtag() directly for events GTM will not forward, so they
 * still reach GA4 — see GTM_FORWARDED_EVENTS. Includes error handling so
 * analytics can never crash the application.
 *
 * @param eventName - Name of the event to track
 * @param eventData - Additional data to include with the event
 */
export const trackEvent = (
  eventName: string,
  eventData: TrackableEventData = {}
): void => {
  try {
    // Ensure dataLayer exists
    if (typeof window === 'undefined') {
      console.warn(
        '🔄 trackEvent: dataLayer not available, event not tracked:',
        eventName
      )
      return
    }

    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || []

    // Prepare the event object
    const event: DataLayerEvent = {
      event: eventName,
      timestamp: Date.now(),
      ...eventData,
    }

    // Push to dataLayer
    window.dataLayer.push(event)

    // Send to GA4 ourselves unless GTM is on this page AND has a tag for this
    // event — that combination, and only it, would otherwise double-count.
    const gtmWillForward =
      hasTagManagerContainer() && GTM_FORWARDED_EVENTS.has(eventName)

    if (!gtmWillForward) {
      // The typed payload interfaces have no index signature; their values are
      // already flat scalars, so this widening is safe.
      sendDirectToGa4(eventName, eventData as GtagEventParams)
    }
  } catch (error) {
    // Log error but don't throw to prevent application crashes
    console.error('🚨 trackEvent: Failed to track event:', eventName, error)
  }
}

/**
 * Tracks signup step selection events
 *
 * @param accountType - Type of account selected ('personal' or 'business')
 */
export const trackSignupSelection = (
  accountType: 'personal' | 'business'
): void => {
  trackEvent('signup_step1_selection', {
    account_type: accountType,
    step: 'selection',
  })
}

/**
 * Tracks signup step progression
 *
 * @param fromStep - Current step number
 * @param toStep - Target step number
 * @param accountType - Type of account being created
 */
export const trackSignupStepProgression = (
  fromStep: number,
  toStep: number,
  accountType?: 'personal' | 'business'
): void => {
  trackEvent('signup_step_progression', {
    from_step: fromStep,
    to_step: toStep,
    account_type: accountType,
  })
}

/**
 * Tracks signup form submission for personal accounts
 *
 * @param email - User email (will be hashed for privacy)
 * @param userType - Type of user (player, admin, team_rep)
 * @param context - Signup context
 * @param contextId - Context ID
 */
export const trackPersonalSignupSubmit = (
  email: string,
  userType: string,
  context: string,
  contextId: string
): void => {
  trackEvent('signup_submit', {
    account_type: 'personal',
    user_type: userType,
    email_hash: createSimpleHash(email),
    context,
    context_id: contextId,
    step: 'personal_complete',
  })
}

/**
 * Tracks business signup step 1 completion (moving from personal info to business info)
 *
 * @param email - User email (will be hashed for privacy)
 * @param context - Signup context
 * @param contextId - Context ID
 */
export const trackBusinessSignupStep1 = (
  email: string,
  context: string,
  contextId: string
): void => {
  trackEvent('signup_step1', {
    account_type: 'business',
    email_hash: createSimpleHash(email),
    context,
    context_id: contextId,
    step: 'business_personal_info_complete',
  })
}

/**
 * Tracks business signup final submission
 *
 * @param email - User email (will be hashed for privacy)
 * @param companyName - Company/organization name
 * @param context - Signup context
 * @param contextId - Context ID
 */
export const trackBusinessSignupSubmit = (
  email: string,
  companyName: string,
  context: string,
  contextId: string
): void => {
  trackEvent('signup_submit', {
    account_type: 'business',
    company_name: companyName,
    email_hash: createSimpleHash(email),
    context,
    context_id: contextId,
    step: 'business_complete',
  })
}

/**
 * Creates wizard event name from action and wizard type
 * @param action - The wizard action to track
 * @param wizard - The wizard type (onboarding or competition)
 * @returns Formatted event name
 */
const buildWizardEventName = (
  action: WizardAction,
  wizard: WizardName
): string => `${action}_${wizard}_wizard`

/**
 * Generic wizard event tracking helper
 * @param wizard - The wizard type
 * @param action - The action being performed
 * @param data - Additional event data
 */
export const trackWizardEvent = (
  wizard: WizardName,
  action: WizardAction,
  data: WizardEventData = {}
): void => {
  const eventName = buildWizardEventName(action, wizard)
  // Set a default context so GTM/GA4 know it's a wizard event
  const base = { context: `${wizard}_wizard` }
  trackEvent(eventName, { ...base, ...data })
}

/**
 * ========== Onboarding Wizard Tracking ==========
 */

/**
 * Tracks onboarding wizard start event
 */
export const trackOnboardingStart = (): void =>
  trackWizardEvent('onboarding', 'start', { step: 'get_started' })

/**
 * Tracks onboarding league layout choice
 * @param choice - The layout choice selected
 */
export const trackOnboardingLeagueLayout = (
  choice: 'simple_league' | 'specialist'
): void =>
  trackWizardEvent('onboarding', 'league_layout', {
    step: 'league_layout',
    choice,
  })

/**
 * Tracks onboarding sports setup completion
 * @param sportCount - Number of sports selected
 */
export const trackOnboardingSportsSetup = (sportCount: number): void =>
  trackWizardEvent('onboarding', 'sports_setup', {
    step: 'sports_setup',
    sport_count: sportCount,
  })

/**
 * Tracks onboarding first competition creation
 * @param opts - Competition details
 */
export const trackOnboardingFirstCompetition = (opts: {
  competition_name: string
  sport: string
  subleague: string
}): void =>
  trackWizardEvent('onboarding', 'first_competition', {
    step: 'first_competition',
    ...opts,
  })

/**
 * Tracks onboarding wizard completion
 */
export const trackOnboardingFinish = (): void =>
  trackWizardEvent('onboarding', 'finish', { step: 'handoff_to_competition' })

/**
 * Tracks onboarding wizard skip event
 * @param currentStep - The step being skipped from
 */
export const trackOnboardingSkip = (currentStep: string): void =>
  trackWizardEvent('onboarding', 'skip', { step: currentStep })

/**
 * Tracks onboarding wizard close event
 * @param currentStep - The step being closed from
 */
export const trackOnboardingClose = (currentStep: string): void =>
  trackWizardEvent('onboarding', 'close', { step: currentStep })

/**
 * Tracks onboarding wizard continue/resume event
 * @param restoredStep - The step being restored to
 */
export const trackOnboardingContinue = (restoredStep: string): void =>
  trackWizardEvent('onboarding', 'continue', { step: restoredStep })

/**
 * ========== Competition Wizard Tracking ==========
 */

/**
 * Tracks competition wizard start event
 */
export const trackCompetitionStart = (): void =>
  trackWizardEvent('competition', 'start', { step: 'structure' })

/**
 * Tracks competition structure choice
 * @param choice - The structure choice selected
 */
export const trackCompetitionStructure = (
  choice: 'single_division' | 'multi_division' | 'conference_divisions'
): void =>
  trackWizardEvent('competition', 'structure', { step: 'structure', choice })

/**
 * Tracks competition division setup
 * @param divisionName - Name of the division being set up
 */
export const trackCompetitionDivision = (divisionName: string): void =>
  trackWizardEvent('competition', 'division', {
    step: 'division',
    division_name: divisionName,
  })

/**
 * Tracks competition conference setup
 * @param count - Number of conferences being set up
 */
export const trackCompetitionConference = (count: number): void =>
  trackWizardEvent('competition', 'conference', {
    step: 'conference',
    conference_count: count,
  })

/**
 * Tracks competition wizard completion
 * @param choice - The completion choice (registration or manual)
 */
export const trackCompetitionFinish = (
  choice: 'registration' | 'manual'
): void => trackWizardEvent('competition', 'finish', { step: 'finish', choice })

/**
 * Tracks competition wizard skip event
 * @param currentStep - The step being skipped from
 */
export const trackCompetitionSkip = (currentStep: string): void =>
  trackWizardEvent('competition', 'skip', { step: currentStep })

/**
 * Tracks competition wizard close event
 * @param currentStep - The step being closed from
 */
export const trackCompetitionClose = (currentStep: string): void =>
  trackWizardEvent('competition', 'close', { step: currentStep })

/**
 * Tracks competition wizard continue/resume event
 * @param restoredStep - The step being restored to
 */
export const trackCompetitionContinue = (restoredStep: string): void =>
  trackWizardEvent('competition', 'continue', { step: restoredStep })

/**
 * ========== Registration Flow Tracking ==========
 */

/**
 * Attributes describing the registrant, sent alongside each registration step.
 *
 * IDs and enums only — never name, email, birth date or any other PII, which
 * Google's terms prohibit in GA4. Free-text values (team/division names) are
 * deliberately excluded too: they are high-cardinality and would degrade the
 * corresponding custom dimension. Join on the IDs instead.
 */
export interface RegistrationStepData {
  registration_id?: string
  registration_type?: string
  is_team_rep?: boolean
  /**
   * Selected player. Absent until a player is chosen, and deliberately omitted
   * for a not-yet-created player (the flow uses a `'new'` sentinel id, which is
   * not a real identifier and would collide across unrelated users in GA4).
   */
  player_id?: string
  /** Selected team. Absent until a team is chosen, and for a new team. */
  team_id?: string
  /**
   * Selected division, as its `schedule_id` — the id the API and the
   * registration session (`session.division_id`) key on, so it joins to our own
   * data. Absent until a division is chosen.
   */
  division_id?: string
}

/**
 * Tracks a registration wizard step view.
 *
 * Emitted once per step the user actually lands on, including via browser
 * back/forward and deep links. `from_step` is undefined for the first step of
 * a session. Steps are identified by URL slug (e.g. 'select-division') rather
 * than by numeric index, so reports stay readable.
 *
 * @param step - Slug of the step being entered
 * @param fromStep - Slug of the step being left, if any
 * @param data - Registrant attributes (IDs/enums only)
 */
export const trackRegistrationStep = (
  step: string,
  fromStep?: string,
  data: RegistrationStepData = {}
): void => {
  trackEvent('registration_step', {
    context: 'registration',
    step,
    from_step: fromStep,
    ...data,
  })
}

/**
 * Returns the platform type based on viewport width
 */
export const getPlatformType = (): string => {
  if (typeof window === 'undefined') {
    return 'web_desktop'
  }
  return window.innerWidth <= 768 ? 'web_mobile' : 'web_desktop'
}

// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Array<DataLayerEvent>
  }
}

/**
 * Window shape for the globals installed by the GA4/GTM snippets in each
 * tenant's index.html. `gtag` is optional: it exists wherever gtag.js is
 * loaded, which is both tenants today, but analytics must never assume it.
 */
type GtagWindow = Window & {
  gtag?: (
    command: 'event',
    eventName: string,
    eventParams: GtagEventParams
  ) => void
}
